import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import Container from '@ui/container/container';
import { useAppSelector } from '@/store/hooks';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { AUDIENCES_VIEW } from '@/types/registration.types';
import AudiencesSearch from './audiences-search';
import AudiencesFilterByPasscodeLists from './audiences-filter-by-passcode-list';
import AudiencesAddButton from '../controls/audiences-add-button';
import OptionalComponent from '@ui/optional-component/optional-component';
import { canEdit } from '../default-passcode-lists';
import AudiencesUploadButton from '../controls/audiences-upload-button';

const getNumberArrayFromParams = (value: string) => {
  const numberArray: number[] = [];

  if (!value || !value.length) return numberArray;

  value.split(',').forEach((item) => {
    const num = parseInt(item.trim());
    if (!isNaN(num)) {
      numberArray.push(num);
    }
  });

  return numberArray;
};

type AudiencesFiltersProps = {
  expandedSearchWidth?: string;
  onAddClick?: () => void;
  onUploadClick?: () => void;
  activeView?: string;
  status?: string;
};

const AudiencesFilters = ({
  expandedSearchWidth,
  onAddClick,
  onUploadClick,
  activeView,
  status,
}: AudiencesFiltersProps) => {
  const passcodeLists = useAppSelector(selectPasscodeLists);

  const [params, setParams] = useSearchParams();
  const paramsObj = Object.fromEntries(params.entries());
  const [filterPasscodeLists, setFilterPasscodeLists] = useState<number[]>(
    getNumberArrayFromParams(paramsObj.lists) || []
  );

  const handleSelectAllPasscodeLists = () => {
    const newLists = passcodeLists.every(
      (list) => !!list.passcode_list && filterPasscodeLists.includes(list?.passcode_list)
    )
      ? [] //all are selected,so deselect all
      : passcodeLists.map((list) => {
          return list.passcode_list || 0;
        }); //all are not selected, so select all

    setFilterPasscodeLists(newLists);
    setParams((prev) => {
      const currParams = Object.fromEntries(prev.entries());
      return {
        ...currParams,
        lists: newLists.join(','),
      };
    });
  };

  const handleSelectPasscodeList = (value: number) => {
    const newLists = filterPasscodeLists.includes(value)
      ? filterPasscodeLists.filter((passcodeList) => passcodeList !== value)
      : [...filterPasscodeLists, value];

    setFilterPasscodeLists(newLists);
    setParams((prev) => {
      const currParams = Object.fromEntries(prev.entries());
      return {
        ...currParams,
        lists: newLists.join(','),
      };
    });
  };

  return (
    <>
      <Container className="flex jc-space-between padding-b-5">
        <Container className="flex jc-flex-end gap-8">
          <AudiencesSearch expandedSearchWidth={expandedSearchWidth} />
          <OptionalComponent display={activeView === AUDIENCES_VIEW.REGISTRANTS}>
            <AudiencesFilterByPasscodeLists
              selectPasscodeList={handleSelectPasscodeList}
              selectAllPasscodeLists={handleSelectAllPasscodeLists}
              filterPasscodeLists={filterPasscodeLists}
            />
          </OptionalComponent>
          {/* TODO IAN: return this to use at the proper time */}
          {/*
          <PasscodeListMembersRemoveDuplicates
            isPasscodeList={isPasscodeList}
            passcodeList={passcodeList}
            reloadMembers={reloadMembers}
          /> */}
          <OptionalComponent display={activeView !== AUDIENCES_VIEW.LISTS}>
            <AudiencesUploadButton
              onAddClick={onUploadClick}
              canEdit={activeView === AUDIENCES_VIEW.REGISTRANTS ? canEdit(status || '') : true}
            />
          </OptionalComponent>
          <AudiencesAddButton
            onAddClick={onAddClick}
            canEdit={activeView === AUDIENCES_VIEW.REGISTRANTS ? canEdit(status || '') : true}
          />
        </Container>
      </Container>
    </>
  );
};

export default AudiencesFilters;
